import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Crown, 
  Clock, 
  Award, 
  Star, 
  CheckCircle, 
  BookOpen, 
  Globe,
  Mail,
  ArrowRight,
  Target,
  Shield,
  TrendingUp,
  Code,
  ExternalLink,
  Users,
  Building,
  FileCheck,
  Briefcase,
  Home,
  ArrowLeft
} from "lucide-react";

const CCSP = () => {
  const keyAreas = [
    {
      icon: <Crown className="w-6 h-6" />,
      title: "Elite-Level Leadership",
      description: "Premier designation recognizing the highest level of converged security expertise"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Strategic Integration",
      description: "Proven ability to integrate physical, cyber, and operational security domains"
    },
    {
      icon: <Building className="w-6 h-6" />,
      title: "Enterprise Implementation",
      description: "Demonstrated leadership in implementing enterprise-wide converged security frameworks"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Cross-Functional Collaboration",
      description: "Expertise in leading collaboration across IT, OT, compliance, and physical security"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Framework Mastery",
      description: "Advanced implementation of ISO 31000, ISO 27001, ISO 22301, GDPR, and NIST standards"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Operational Excellence",
      description: "Proven track record in deploying Zero Trust Architecture and SASE frameworks"
    }
  ];

  const benefits = [
    "Premier designation recognizing elite-level leadership",
    "Strategic integration and operational excellence recognition",
    "Chartered status from recognized partnered institutions",
    "Verification of real-world framework implementation",
    "Industry recognition as architect of resilience and compliance",
    "Elite network access and professional recognition"
  ];

  const eligibilityCriteria = [
    {
      title: "Valid Certifications",
      description: "Hold current CSCE, CSP, and CSL certifications",
      icon: <Award className="w-5 h-5 text-purple-600" />
    },
    {
      title: "Leadership Experience", 
      description: "Minimum 5 years in managerial/leadership role in IT, cybersecurity, physical security, or risk management",
      icon: <Users className="w-5 h-5 text-purple-600" />
    },
    {
      title: "Active CSP Role",
      description: "Demonstrate active fulfillment of Converged Security Practitioner role per organizational blueprint",
      icon: <Briefcase className="w-5 h-5 text-purple-600" />
    },
    {
      title: "Fellowship Status",
      description: "Active holder of fellowship or chartered status from recognized partnered institution",
      icon: <Crown className="w-5 h-5 text-purple-600" />
    },
    {
      title: "Framework Implementation",
      description: "Verifiable evidence of leading implementation of Converged Security Framework within enterprise",
      icon: <Building className="w-5 h-5 text-purple-600" />
    }
  ];

  const frameworkRequirements = [
    "Strategic integration of physical, cyber, and operational domains",
    "Cross-functional collaboration across IT, OT, compliance, and physical security",
    "Adoption of ISO 31000, ISO 27001, ISO 22301, GDPR, and NIST standards",
    "Integration of SIEM, PSIM, IAM, and open architecture systems",
    "Maintenance of unified risk register and shared KPIs",
    "Deployment of Zero Trust Architecture and SASE frameworks",
    "Execution of scenario-based training and incident simulations",
    "Alignment with strategic business goals and executive endorsement"
  ];

  const idealFor = [
    "Senior security executives with comprehensive certification portfolio",
    "Chief Security Officers implementing enterprise frameworks",
    "Security consultants with proven implementation experience",
    "Risk management leaders with chartered professional status",
    "Enterprise architects designing converged security solutions",
    "Strategic advisors driving organizational security transformation"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-700 to-indigo-800 text-white py-16 relative">
        {/* Navigation Bar - Upper Right */}
        <div className="absolute top-4 right-6 flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            onClick={() => window.open('https://convergedsecurity.es/', '_blank')}
          >
            <Home className="w-4 h-4 mr-1" />
            Home
          </Button>
        </div>
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-white/20 text-white border-white/30 mb-4">
                Premier / Chartered Designation
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                C-CSP
              </h1>
              <h2 className="text-2xl md:text-3xl font-semibold mb-4 text-purple-100">
                Chartered Converged Security Practitioner
              </h2>
              <p className="text-xl text-purple-100 mb-8 leading-relaxed">
                The premier designation recognizing elite-level leadership, strategic integration, and operational excellence in implementing enterprise-wide converged security frameworks. This credential represents the culmination of a rigorous professional journey.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="bg-white text-purple-700 hover:bg-purple-50"
                  onClick={() => window.open('https://pay.sumup.com/b2c/QVSOSICX', '_blank')}
                >
                  <Star className="w-5 h-5 mr-2" />
                  Pay Application - €50
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10"
                  onClick={() => window.open('https://pay.sumup.com/b2c/QMN044J9', '_blank')}
                >
                  <Award className="w-5 h-5 mr-2" />
                  Pay Final - €450
                </Button>
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  onClick={() => window.open('/#/ccsp-embed-docs', '_blank')}
                >
                  <Code className="w-4 h-4 mr-2" />
                  Get Embed Code
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  onClick={() => window.open('/#/ccsp-embed', '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Preview Embed
                </Button>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-80 h-80 rounded-full bg-gradient-to-br from-purple-600 to-indigo-700 flex items-center justify-center drop-shadow-2xl">
                  <Crown className="w-32 h-32 text-white" />
                </div>
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-400/20 to-indigo-400/20 rounded-full blur-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Facts */}
      <div className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-6">
            <Card className="text-center border-purple-200">
              <CardContent className="pt-6">
                <Star className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-bold text-2xl text-gray-900">€500</h3>
                <p className="text-gray-600">Total Fee</p>
              </CardContent>
            </Card>
            <Card className="text-center border-purple-200">
              <CardContent className="pt-6">
                <Clock className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-bold text-2xl text-gray-900">3 Years</h3>
                <p className="text-gray-600">Chartered Award</p>
              </CardContent>
            </Card>
            <Card className="text-center border-purple-200">
              <CardContent className="pt-6">
                <Users className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-bold text-2xl text-gray-900">5+ Years</h3>
                <p className="text-gray-600">Leadership Experience</p>
              </CardContent>
            </Card>
            <Card className="text-center border-purple-200">
              <CardContent className="pt-6">
                <FileCheck className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-bold text-2xl text-gray-900">30 Days</h3>
                <p className="text-gray-600">Review Period</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Key Competencies */}
      <div className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Elite-Level Expertise
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              C-CSP represents the pinnacle of converged security achievement, recognizing architects of resilience, compliance, and cross-functional collaboration
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {keyAreas.map((area, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300 border-purple-100">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
                      {area.icon}
                    </div>
                    <CardTitle className="text-lg">{area.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{area.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Eligibility Criteria */}
      <div className="py-16 bg-gradient-to-r from-purple-700 to-indigo-800 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Eligibility Criteria
            </h2>
            <p className="text-xl text-purple-100 max-w-3xl mx-auto">
              C-CSP candidates must meet all of the following stringent requirements
            </p>
          </div>
          
          <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-6">
            {eligibilityCriteria.map((criteria, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-sm border-white/20">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-white/20 rounded-lg">
                      {criteria.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2 text-white">{criteria.title}</h3>
                      <p className="text-purple-100">{criteria.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Framework Implementation Requirements */}
      <div className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Framework Implementation Requirements
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Candidates must provide documentation demonstrating leadership in the following areas
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {frameworkRequirements.map((requirement, index) => (
              <Card key={index} className="border-purple-100 hover:border-purple-300 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">{requirement}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Application Process */}
      <div className="py-16 bg-gradient-to-br from-purple-50 to-indigo-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Application Process & Fees
            </h2>
            <p className="text-xl text-gray-600">
              Rigorous evaluation process ensuring the highest standards of professional excellence
            </p>
          </div>
          
          <Card className="border-purple-200 shadow-lg">
            <CardContent className="pt-8">
              <div className="grid md:grid-cols-4 gap-6 mb-8">
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-purple-600 font-bold">1</span>
                  </div>
                  <h3 className="font-semibold mb-2">Eligibility Check</h3>
                  <p className="text-sm text-gray-600">Contact administration team for initial assessment</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-purple-600 font-bold">2</span>
                  </div>
                  <h3 className="font-semibold mb-2">Application</h3>
                  <p className="text-sm text-gray-600">Submit application with €50 non-refundable fee</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-purple-600 font-bold">3</span>
                  </div>
                  <h3 className="font-semibold mb-2">Review</h3>
                  <p className="text-sm text-gray-600">Comprehensive evaluation up to 30 days</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-purple-600 font-bold">4</span>
                  </div>
                  <h3 className="font-semibold mb-2">Acceptance</h3>
                  <p className="text-sm text-gray-600">Final fee of €450 upon acceptance</p>
                </div>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-4 text-purple-700">Fee Structure</h3>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">€50</div>
                    <div className="text-gray-600">Application Fee</div>
                    <div className="text-xs text-gray-500">(Non-refundable)</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">€450</div>
                    <div className="text-gray-600">Final Fee</div>
                    <div className="text-xs text-gray-500">(Upon acceptance)</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">€500</div>
                    <div className="text-gray-600">Total Cost</div>
                    <div className="text-xs text-gray-500">(Complete process)</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Ideal Candidates */}
      <div className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Perfect For
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              C-CSP is reserved for the most accomplished professionals in converged security leadership
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {idealFor.map((candidate, index) => (
              <Card key={index} className="border-purple-100 hover:border-purple-300 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <ArrowRight className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700">{candidate}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="py-16 bg-gradient-to-br from-purple-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready for Elite Recognition?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join the exclusive ranks of Chartered Converged Security Practitioners
          </p>
          
          <Card className="border-purple-200 shadow-lg">
            <CardContent className="pt-8">
              <div className="mb-8">
                <h3 className="text-xl font-bold mb-4 text-purple-700">Before You Apply</h3>
                <p className="text-gray-600 mb-4">
                  We recommend checking your eligibility with our administration team before beginning the formal application process.
                </p>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-purple-700 font-medium">
                    Contact: info@convergedsecurity.es for eligibility verification
                  </p>
                </div>
              </div>
              
              <div className="flex flex-wrap justify-center gap-4">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                  onClick={() => window.open('https://pay.sumup.com/b2c/QVSOSICX', '_blank')}
                >
                  <Star className="w-5 h-5 mr-2" />
                  Pay Application - €50
                </Button>
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                  onClick={() => window.open('https://pay.sumup.com/b2c/QMN044J9', '_blank')}
                >
                  <Award className="w-5 h-5 mr-2" />
                  Pay Final - €450
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-purple-300 text-purple-600 hover:bg-purple-50"
                  onClick={() => window.open('mailto:info@convergedsecurity.es?subject=C-CSP Eligibility Check&body=Hello, I would like to check my eligibility for the C-CSP (Chartered Converged Security Practitioner) certification. I believe I meet the requirements and would like to discuss the application process. Please provide guidance on next steps.', '_blank')}
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Check Eligibility
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-purple-300 text-purple-600 hover:bg-purple-50"
                  onClick={() => window.open('mailto:info@convergedsecurity.es?subject=C-CSP Brochure Request&body=Hello, I would like to download the C-CSP (Chartered Converged Security Practitioner) certification brochure. Please send me the detailed program information and requirements.', '_blank')}
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  Download Brochure
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-gray-400 mb-2">
            © 2025 Converged Security Institute (CSI). All rights reserved.
          </p>
          <p className="text-sm text-gray-500">
            C-CSP certification adheres to European Union GDPR, Cybersecurity Act, and NIS Directive
          </p>
        </div>
      </div>
    </div>
  );
};

export default CCSP;